import { useState } from "react";
import { Currency } from "../../constants";

interface TransactionBreakdownProps {
  baseAmount: string;
  baseCurrency: Currency;
  quoteAmount: string;
  quoteCurrency: Currency;
  rate: number;
  fee: number;
  grossAmount: number; // Amount before fee deduction
}

export function TransactionBreakdown({
  baseAmount,
  baseCurrency,
  quoteAmount,
  quoteCurrency,
  rate,
  fee,
  grossAmount,
}: TransactionBreakdownProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!baseAmount || !quoteAmount) {
    return null;
  }

  const formattedBaseAmount = Number(baseAmount).toLocaleString("en-US");
  const formattedGrossAmount = grossAmount.toLocaleString("en-US");
  const formattedFee = fee.toLocaleString("en-US");
  const formattedQuoteAmount = Number(quoteAmount).toLocaleString("en-US");

  return (
    <div className="transaction-breakdown bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            Calculation Breakdown
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 text-sm">
          {/* Step 1: Base Amount */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">You send</span>
            <span className="font-semibold text-gray-900">
              {formattedBaseAmount} {baseCurrency}
            </span>
          </div>

          {/* Step 2: Exchange Rate Multiplication */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Exchange rate</span>
            <span className="text-gray-700">× {rate.toFixed(4)}</span>
          </div>

          {/* Step 3: Gross Amount */}
          <div className="flex items-center justify-between border-t pt-3">
            <span className="text-gray-600">Converted amount</span>
            <span className="font-medium text-gray-900">
              {formattedGrossAmount} {quoteCurrency}
            </span>
          </div>

          {/* Step 4: Fee Deduction */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Transaction fee</span>
            <span className="text-red-600 font-medium">
              − {formattedFee} {quoteCurrency}
            </span>
          </div>

          {/* Step 5: Final Amount */}
          <div className="flex items-center justify-between border-t-2 border-primary pt-3">
            <span className="text-gray-700 font-semibold">
              Recipient receives
            </span>
            <span className="text-lg font-bold text-primary">
              {formattedQuoteAmount} {quoteCurrency}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
